import { ArrowSquareOutIcon, CaretDownIcon, LinkBreakIcon, PlusIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { type DistributiveOmit } from '@tanstack/react-query'
import classNames from 'classnames'
import { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { useImmer } from 'use-immer'
import { z } from 'zod'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import type { AreEquivalent } from '../../types'
import {
  useDeleteRepositoryIntegration,
  useGetRepositoryIntegrations,
  useSuspenseGetRepositoryIntegrations,
} from '../../utils/api-client/api-hooks'
import type { RepositoryIntegration } from '../../utils/api-client/api-schemas'
import { Repository } from '../../utils/api-client/api-schemas'
import {
  convertIntegrationTypeToTool,
  getRepositoryIdentifierLabel,
  getRepositoryName,
  getRepositoryOrganization,
  getRepositoryTypeDisplayName,
  getToolName,
} from '../../utils/repository-utils'
import { GhostButton } from '../buttons/ghost-button'
import { DeleteRepositoryDialog } from '../delete-repository-dialog.tsx'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { InputGroup } from '../input/input-group'
import { SelectBox } from '../input/select-box'
import { TextInput } from '../input/text-input'
import { RemoveIntegrationModal } from '../modals/remove-integration-modal.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { ToolIcon } from '../third-party-branding/tool-icon'
import { AddFindingsDrawer } from './add-findings-drawer'
import * as styles from './add-findings-drawer.css'
import * as footerStyles from './floating-footer.css'
import { FloatingFooter } from './floating-footer.tsx'
import { useIsMapToolFormOffered } from './map-tool-integration-form.hooks.ts'
import {
  type MapToolIntegrationFormValues,
  MapToolIntegrationForm,
  useMapToolIntegrationForm,
} from './map-tool-integration-form.tsx'
import * as localStyles from './repository-settings-drawer.css'

const RepositoryTypeSchema = z.enum(['github', 'gitlab', 'azure', 'bitbucket', 'git'])
true satisfies AreEquivalent<z.infer<typeof RepositoryTypeSchema>, Repository['type']>

type RepositorySettingsDrawerProps = {
  repository: DistributiveOmit<Repository, '_links'>
  onCloseDrawer: () => unknown
  onUploadClick: () => unknown
}

export const RepositorySettingsDrawer: React.FC<RepositorySettingsDrawerProps> = ({
  repository,
  onCloseDrawer,
  onUploadClick,
}) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [showDeleteRepositoryDialog, setShowDeleteRepositoryDialog] = useState(false)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [showMapNewForm, setShowMapNewForm] = useState(false)
  const [expandedToolIds, updateExpandedToolIds] = useImmer(new Set<string>())
  const [integrationToRemove, setIntegrationToRemove] = useState<string | null>(null)

  // The gate below and the form it opens must agree on whether the form has anything to offer, so
  // both read the same question from the same place: which tools have at least one instance.
  const isMapToolFormOffered = useIsMapToolFormOffered()
  const { data: repositoryIntegrations } = useGetRepositoryIntegrations({ repositoryId: repository.id })

  const { addToast } = useToastService()
  const showRemoveIntegrationError = useShowMutationError({ prefix: 'Failed to remove integration' })
  const deleteRepositoryIntegration = useDeleteRepositoryIntegration({
    onSuccess: () => {
      addToast({
        message: <>Integration removed successfully.</>,
        variant: 'success',
      })
    },
    onError: showRemoveIntegrationError,
  })

  const handleRemoveMapping = useCallback((integrationId: string) => {
    setIntegrationToRemove(integrationId)
  }, [])

  const handleConfirmRemoveMapping = useCallback(() => {
    if (integrationToRemove) {
      deleteRepositoryIntegration.mutate({ repositoryId: repository.id, integrationId: integrationToRemove })
      setIntegrationToRemove(null)
    }
  }, [integrationToRemove, deleteRepositoryIntegration, repository.id])

  const handleCancelRemoveMapping = useCallback(() => {
    setIntegrationToRemove(null)
  }, [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onCloseDrawer()
      }
    },
    [onCloseDrawer]
  )

  const handleUploadClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      onCloseDrawer()
      onUploadClick()
    },
    [onCloseDrawer, onUploadClick]
  )

  const handleMapNewClick = useCallback(() => {
    setShowMapNewForm(true)
  }, [])

  const handleCloseMapNewForm = useCallback(() => {
    setShowMapNewForm(false)
  }, [])

  const toggleToolExpansion = useCallback((toolId: string) => {
    updateExpandedToolIds(draft => {
      if (draft.has(toolId)) {
        draft.delete(toolId)
      } else {
        draft.add(toolId)
      }
    })
  }, [])

  const repositoryOrganization = getRepositoryOrganization(repository)
  const repositoryName = getRepositoryName(repository)

  return (
    <>
      <Dialog.Root open={true} modal={false} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
          >
            <Dialog.Title className={`${styles.title} ${localStyles.title}`}>
              <div style={{ display: 'flex' }} className={localStyles.repoName}>
                <SCMIcon type={repository.type} size="small" />
                {repository.name}
              </div>
              Repository settings
            </Dialog.Title>
            <Dialog.Description className={`${styles.description} ${localStyles.separator}`}>
              Configure repository analysis and notification settings
            </Dialog.Description>

            <ErrorBoundary fallbackTitle="Failed to display settings">
              <div style={{ display: 'flex', width: '100%' }} className={localStyles.settingsContainer}>
                <InputGroup title="Connection type">
                  <SelectBox
                    label="Integration"
                    value={repository.type}
                    disabled={true}
                    options={RepositoryTypeSchema.options.map(type => ({
                      value: type,
                      label: (
                        <span className={localStyles.selectItemText}>
                          <SCMIcon type={type} size="small" />
                          {getRepositoryTypeDisplayName(type)}
                        </span>
                      ),
                    }))}
                  />
                  <SelectBox
                    label="Organization"
                    value={repositoryOrganization}
                    disabled={true}
                    options={[
                      {
                        value: repositoryOrganization,
                        label: repositoryOrganization,
                      },
                    ]}
                  />
                  <SelectBox
                    label="Project"
                    value={repositoryName}
                    disabled={true}
                    options={[
                      {
                        value: repositoryName,
                        label: repositoryName,
                      },
                    ]}
                  />
                </InputGroup>

                <InputGroup
                  title="Mapped tools"
                  titleAction={
                    isMapToolFormOffered ? (
                      <GhostButton
                        type="default"
                        size="small"
                        state={showMapNewForm ? 'disabled' : 'default'}
                        onClick={handleMapNewClick}
                      >
                        <PlusIcon
                          size={16}
                          weight="light"
                          style={{
                            marginRight: '4px',
                            color: mappedColors.neutral.halo,
                          }}
                        />
                        Map new
                      </GhostButton>
                    ) : undefined
                  }
                >
                  {showMapNewForm && (
                    <Suspense fallback={null}>
                      <MapToolIntegrationFormHost
                        repositoryId={repository.id}
                        onSuccess={handleCloseMapNewForm}
                        onCancel={handleCloseMapNewForm}
                        onUploadClick={handleUploadClick}
                      />
                    </Suspense>
                  )}

                  <MappedToolList
                    repositoryIntegrations={repositoryIntegrations?._embedded.items}
                    expandedToolIds={expandedToolIds}
                    onToggleToolExpansion={toggleToolExpansion}
                    onRemoveMapping={handleRemoveMapping}
                  />
                </InputGroup>
              </div>
            </ErrorBoundary>
          </Dialog.Content>
          <FloatingFooter drawerContentRef={drawerContentRef}>
            <ActionButtons
              onOpenDeleteRepositoryDialog={() => setShowDeleteRepositoryDialog(true)}
              onCloseDrawer={onCloseDrawer}
            />
          </FloatingFooter>
        </Dialog.Portal>
      </Dialog.Root>

      {showDeleteRepositoryDialog && (
        <DeleteRepositoryDialog
          repository={repository}
          onCloseDeleteRepositoryDialog={() => setShowDeleteRepositoryDialog(false)}
          onDeleteRepositorySuccess={onCloseDrawer}
        />
      )}

      {showUploadForm && (
        <AddFindingsDrawer repositoryId={repository.id} onCloseDrawer={() => setShowUploadForm(false)} />
      )}

      {integrationToRemove && (
        <RemoveIntegrationModal
          repository={repository}
          repositoryIntegrations={repositoryIntegrations?._embedded.items}
          integrationToRemove={integrationToRemove}
          setIntegrationToRemove={setIntegrationToRemove}
          onCancelRemoveMapping={handleCancelRemoveMapping}
          onConfirmRemoveMapping={handleConfirmRemoveMapping}
        />
      )}
    </>
  )
}

type MapToolIntegrationFormHostProps = {
  repositoryId: string
  onSuccess: () => unknown
  onCancel: () => unknown
  onUploadClick: (e: React.MouseEvent) => unknown
}

const MapToolIntegrationFormHost: React.FC<MapToolIntegrationFormHostProps> = ({
  repositoryId,
  onSuccess,
  onCancel,
  onUploadClick,
}) => {
  const { data: repositoryIntegrations } = useSuspenseGetRepositoryIntegrations({ repositoryId })

  const formDefaultValues = useMemo<Partial<MapToolIntegrationFormValues> | undefined>(() => {
    const items = repositoryIntegrations._embedded?.items ?? []
    const sonar = items.find(i => i.type === 'sonar')
    if (sonar) {
      return { selectedTool: 'sonar', projectKey: sonar.project_key }
    }
    const checkmarx = items.find(i => i.type === 'checkmarx')
    if (checkmarx) {
      return { selectedTool: 'checkmarx', projectKey: checkmarx.project_id }
    }
    const appscan = items.find(i => i.type === 'appscan')
    if (appscan) {
      return { selectedTool: 'appscan', projectKey: appscan.application_id }
    }
    const blackDuckHub = items.find(i => i.type === 'black-duck-hub')
    if (blackDuckHub) {
      return {
        selectedTool: 'black_duck_hub_sca',
        projectKey: blackDuckHub.project_id,
        versionId: blackDuckHub.version_id,
      }
    }
    const checkmarxEnterprise = items.find(i => i.type === 'checkmarx-enterprise')
    if (checkmarxEnterprise) {
      return { selectedTool: 'checkmarx_enterprise', projectKey: String(checkmarxEnterprise.project_id) }
    }
    return undefined
  }, [repositoryIntegrations])

  const form = useMapToolIntegrationForm(formDefaultValues)

  return (
    <form.AppForm>
      <MapToolIntegrationForm
        repositoryId={repositoryId}
        onSuccess={onSuccess}
        onCancel={onCancel}
        onUploadClick={onUploadClick}
      />
    </form.AppForm>
  )
}

type MappedToolListProps = {
  repositoryIntegrations?: DistributiveOmit<RepositoryIntegration, '_links'>[]
  expandedToolIds: Set<string>
  onToggleToolExpansion: (toolId: string) => unknown
  onRemoveMapping: (integrationId: string) => unknown
}

const MappedToolList: React.FC<MappedToolListProps> = ({
  repositoryIntegrations,
  expandedToolIds,
  onToggleToolExpansion,
  onRemoveMapping,
}) => {
  return repositoryIntegrations?.length ? (
    <div className={localStyles.mappedToolsContainer}>
      {repositoryIntegrations.map(integration => (
        <MappedTool
          key={integration.id}
          integration={integration}
          expandedToolIds={expandedToolIds}
          onToggleToolExpansion={onToggleToolExpansion}
          onRemoveMapping={onRemoveMapping}
        />
      ))}
    </div>
  ) : (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
      className={localStyles.noToolsContainer}
      data-testid="no-tools-detected"
    >
      <span className={localStyles.italicText}>No tools detected</span>
    </div>
  )
}

type MappedToolProps = {
  integration: DistributiveOmit<RepositoryIntegration, '_links'>
  expandedToolIds: Set<string>
  onToggleToolExpansion: (toolId: string) => unknown
  onRemoveMapping: (integrationId: string) => unknown
}

const MappedTool: React.FC<MappedToolProps> = ({
  integration,
  expandedToolIds,
  onToggleToolExpansion,
  onRemoveMapping,
}) => {
  const getIntegrationId = (integration: DistributiveOmit<RepositoryIntegration, '_links'>): string | undefined => {
    return (
      match(integration)
        .with({ type: 'sonar' }, integration => integration.project_key)
        .with({ type: 'checkmarx' }, integration => integration.project_id)
        .with({ type: 'appscan' }, integration => integration.application_id)
        .with({ type: 'veracode' }, integration => integration.application_guid)
        .with({ type: 'arnica' }, integration => integration.asset_key)
        .with({ type: 'datadog' }, integration => integration.repository_id)
        .with({ type: 'polaris' }, () => undefined)
        // The project UUID alone, to match the "Project ID" label this feeds. A Hub mapping also
        // carries a version UUID, which this single-identifier row has nowhere to put; the mapping
        // form is where both are seen together.
        .with({ type: 'black-duck-hub' }, integration => integration.project_id)
        .with({ type: 'checkmarx-enterprise' }, integration => String(integration.project_id))
        .exhaustive()
    )
  }

  const tool = convertIntegrationTypeToTool(integration.type)
  const identifierLabel = getRepositoryIdentifierLabel(tool)

  return (
    <div className={localStyles.mappedToolItem}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
        className={localStyles.mappedToolHeader}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'start', width: '100%' }}>
          <span className={localStyles.mappedToolName}>
            <ToolIcon tool={tool} size="medium" />
            {getToolName(tool)}
          </span>
          {'html_url' in integration && integration.html_url ? (
            <a
              href={integration.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className={localStyles.projectKeyText}
            >
              <div className={localStyles.flexCenterGap4}>
                {getIntegrationId(integration)}
                <ArrowSquareOutIcon size={12} weight="light" style={{ color: mappedColors.neutral.neutral10 }} />
              </div>
            </a>
          ) : (
            <span className={localStyles.projectKeyText}>{getIntegrationId(integration)}</span>
          )}
        </div>
        <button
          className={localStyles.caretButton}
          onClick={() => onToggleToolExpansion(integration.id)}
          aria-label="Toggle tool details"
        >
          <CaretDownIcon
            size={16}
            weight="light"
            style={{
              transform: expandedToolIds.has(integration.id) ? undefined : 'rotate(-90deg)',
            }}
          />
        </button>
      </div>
      {expandedToolIds.has(integration.id) && (
        <div className={localStyles.toolDetails}>
          {/* One field driven by the shared label table and `getIntegrationId`, rather than an arm per
              tool — the per-arm version had no `veracode` case, so an expanded Veracode mapping
              showed no identifier at all even though its GUID was right there. */}
          {identifierLabel !== undefined && (
            <TextInput
              id={`identifier-${integration.id}`}
              label={identifierLabel}
              value={getIntegrationId(integration) ?? ''}
              disabled={true}
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'end', width: '100%', marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LinkBreakIcon size={16} weight="light" style={{ color: mappedColors.neutral.halo }} />
              <span className={localStyles.removeMappingText} onClick={() => onRemoveMapping(integration.id)}>
                Remove mapping
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type ActionButtonsProps = {
  onOpenDeleteRepositoryDialog: () => unknown
  onCloseDrawer: () => unknown
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ onOpenDeleteRepositoryDialog, onCloseDrawer }) => {
  return (
    <>
      <GhostButton
        icon={<TrashSimpleIcon size={16} weight="light" />}
        type="destructive"
        onClick={onOpenDeleteRepositoryDialog}
      >
        Delete repository
      </GhostButton>
      <div style={{ marginLeft: 'auto' }}>
        <GhostButton type="secondary" onClick={onCloseDrawer}>
          Close
        </GhostButton>
      </div>
    </>
  )
}
